/**
 * useCommentsDrawer Hook
 *
 * Generic hook for managing comments drawer state and operations.
 * Works with any entity type that has a comments field.
 */

import {
  CommentsDrawerProps,
  EnhancedComment,
  UseCommentsDrawerProps,
  UseCommentsDrawerType,
} from '@deckManagement/components/Comments/CommentsDrawer.types';
import { dateIsAfter } from '@utils/dateHelpers';
import { isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * useCommentsDrawer Hook
 *
 * Generic hook for managing comments drawer state and operations.
 * Works with any entity type that has a `comments: Comment[]` field from the DeckManagement API.
 * @param props
 */
const useCommentsDrawer = <T extends { comments?: (EnhancedComment | null)[] | null }>(
  props: UseCommentsDrawerProps<T>,
): UseCommentsDrawerType<T> => {
  const { key, selectedItem, onSave, getItemId, getItemLabel, maxAgeInDays = 60 } = props;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [targetItem, setTargetItem] = useState<T | null>(null);
  const [delayedUnsetTimeout, setDelayedUnsetTimeout] = useState<NodeJS.Timeout | null>(null);
  const prevKey = useRef(key);

  /**
   * Cancels the delayed unset timeout if it exists
   */
  const cancelUnsetTimeout = useCallback(() => {
    if (delayedUnsetTimeout) {
      clearTimeout(delayedUnsetTimeout);
      setDelayedUnsetTimeout(null);
    }
  }, [delayedUnsetTimeout]);

  /**
   * Opens the drawer for the given item, or for the selected item when omitted. Passing the item lets a
   * caller open the drawer for an item it has just selected, before `selectedItem` has re-rendered.
   * @param item - The item to open the drawer for (defaults to `selectedItem`)
   */
  const openDrawer = useCallback(
    (item: T | null = selectedItem) => {
      cancelUnsetTimeout();
      if (item) {
        setDrawerOpen(true);
        setTargetItem(item);
      }
    },
    [cancelUnsetTimeout, selectedItem],
  );

  /**
   * Closes the drawer and resets the target item
   */
  const onClose = useCallback(() => {
    setDrawerOpen(false);

    // Adding delay to prevent displaying 'undefined' while the drawer is transitioning to a fully-closed state
    setDelayedUnsetTimeout(
      setTimeout(() => {
        setTargetItem(null);
      }, 1000),
    );

    return () => cancelUnsetTimeout();
  }, [cancelUnsetTimeout]);

  /**
   * Effect for handling changes in the key prop
   */
  useEffect(() => {
    if (prevKey.current !== key) {
      onClose();
      prevKey.current = key;
    }
  }, [onClose, key]);

  /**
   * Saves the comments and updates the target item
   * @param updatedComments - The updated comments array
   */
  const saveComments: CommentsDrawerProps['onSave'] = useCallback(
    (updatedComments) => {
      if (targetItem) {
        console.debug('comments:', updatedComments);
        const filteredComments = updatedComments ? updatedComments.filter((comment) => !isNil(comment)) : [];
        onSave(getItemId(targetItem), filteredComments);
        // Update the local snapshot with new comments
        setTargetItem((prev) => (prev ? { ...prev, comments: updatedComments } : null));
      }
    },
    [targetItem, onSave, getItemId],
  );

  /**
   * Gets the comments for the target item, filtering out null comments and those older than `maxAgeInDays`
   * @returns The filtered comments array
   */
  const comments = useMemo(() => {
    const commentsList = targetItem?.comments || [];
    return (
      commentsList
        // Remove null comments
        .filter((comment) => !isNil(comment))
        // Remove comments older than `maxAgeInDays`
        .filter((comment) => maxAgeInDays && dateIsAfter(comment.timestamp, maxAgeInDays))
        // Ensure all comments have a `metadata` field
        .map((comment) => {
          const metadata = { isPendingSave: false } as EnhancedComment['metadata'];
          if (!isNil(comment?.metadata)) {
            metadata.isPendingSave = comment.metadata.isPendingSave;
            metadata.isPendingDelete = comment.metadata.isPendingDelete;
          }
          const enhancedComment: EnhancedComment = {
            ...comment,
            metadata,
            __typename: 'Comment',
          };
          return enhancedComment;
        })
    );
  }, [maxAgeInDays, targetItem?.comments]);

  const subjectLabel = targetItem && getItemLabel ? getItemLabel(targetItem) : undefined;

  return {
    open: drawerOpen,
    targetItem,
    subjectLabel,
    openDrawer,
    onClose,
    onSave: saveComments,
    comments,
    maxAgeInDays,
  };
};

export default useCommentsDrawer;
